import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { completedLevelCount, gcomprisLevelRewards } from '../utils/gcompris';

const NO_PROGRESS = { nextLevel: 1, completedLevels: [] };

const INITIAL_STATE = {
  loading: true,
  reward: { coinsPerGame: 0, coinsPerNewLevel: 0, levelCoins: [] },
  stats: { activitiesPlayed: 0, sessions: 0, levelsCompleted: 0, playTimeMs: 0 },
  byActivity: {},
};

// Progress reads are best-effort: a failed request just means "nothing recorded yet".
async function getData(url) {
  try {
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    return null;
  }
}

/**
 * The signed-in student's GCompris progress, from the same endpoints
 * GComprisLauncher uses: the per-activity summary (completed levels, sessions,
 * play time), the level GCompris will resume each played activity at, and the
 * coins awarded per new level.
 *
 * `progressFor(name)` gives `{ nextLevel, completedLevels }` for any activity
 * (level 1 / nothing completed when it hasn't been played), and `rewardsFor(name)` its
 * `{ coinsForLevel(level), sameEveryLevel }` (see gcomprisLevelRewards).
 */
export default function useGComprisProgress() {
  const { user } = useAuth();
  const studentId = String(user?._id || user?.id || '');
  const [state, setState] = useState(INITIAL_STATE);
  const latestRequest = useRef(0);

  const refresh = useCallback(async () => {
    const request = ++latestRequest.current;
    const [summary, reward] = await Promise.all([
      studentId ? getData(`/api/gcompris/summary/${studentId}`) : null,
      getData('/api/gcompris/settings/reward'),
    ]);

    // The summary has one row per (assignment, activity); fold them into one per activity.
    const completedByActivity = new Map();
    for (const item of summary?.activities || []) {
      const completed = completedByActivity.get(item.activityName) || new Set();
      (item.completedLevels || []).forEach((level) => completed.add(level));
      completedByActivity.set(item.activityName, completed);
    }

    const played = await Promise.all(
      [...completedByActivity].map(async ([activityName, completed]) => {
        const completedLevels = [...completed].sort((a, b) => a - b);
        const fallbackLevel = (completedLevels[completedLevels.length - 1] || 0) + 1;
        const lastLevel = await getData(`/api/gcompris/lastlevel/${encodeURIComponent(activityName)}`);
        return [activityName, { completedLevels, nextLevel: Math.max(1, Number(lastLevel) || fallbackLevel) }];
      })
    );

    if (request !== latestRequest.current) return; // a newer refresh superseded this one
    setState({
      loading: false,
      reward: { coinsPerGame: reward?.coinsPerGame, coinsPerNewLevel: reward?.coinsPerNewLevel, levelCoins: reward?.levelCoins || [] },
      stats: {
        activitiesPlayed: played.length,
        sessions: summary?.totals?.sessions || 0,
        levelsCompleted: played.reduce(
          (sum, [, progress]) => sum + completedLevelCount(progress.completedLevels, progress.nextLevel),
          0
        ),
        playTimeMs: summary?.totals?.durationMs || 0,
      },
      byActivity: Object.fromEntries(played),
    });
  }, [studentId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const progressFor = useCallback(
    (activityName) => state.byActivity[activityName] || NO_PROGRESS,
    [state.byActivity]
  );
  const rewardsFor = useCallback((activityName) => gcomprisLevelRewards(state.reward, activityName), [state.reward]);

  return {
    loading: state.loading,
    stats: state.stats,
    progressFor,
    rewardsFor,
    refresh,
  };
}
